import deepmerge from 'deepmerge';
import { cloneDeep, concat, get } from 'lodash';
import { useCallback, useDebugValue, useEffect, useMemo } from 'react';
import { Filters, SortingRule } from 'react-table';

import {
  CustomFilterMapping,
  ITableStateBag,
  usePrepareFilters,
  useQueryTableData,
} from '@work4all/components';

import {
  useDataMutation,
  useRefetchOnEntityChanges,
  useUser,
} from '@work4all/data';
import { useDisableApolloAutoInvalidation } from '@work4all/data//lib/apollo-extras/use-disable-apollo-auto-invalidation';
import {
  ALLOWED_CUSTOM_FIELD_TYPES,
  createCustomColumn,
  useCustomFieldsConfigContext,
} from '@work4all/data/lib/custom-fields';

import { Work4AllEntity } from '@work4all/models/lib/additionalEnums/Work4AllEntity.entity';
import { entityDefinition } from '@work4all/models/lib/Classes/entityDefinitions';
import {
  DataRequest,
  SortDirection,
  SortOption,
} from '@work4all/models/lib/DataProvider';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import {
  IColumnNumberFilterConfig,
  ICustomCellConfigBase,
} from '@work4all/models/lib/table-schema/table-schema';

import { DataTableColumnConfig } from './table/DataTableColumnConfig';
import { useSelectedTableRows } from './use-selected-table-rows';
import {
  isReadStatusModifier,
  IUseTableConfigOptions,
  useTableConfig,
} from './use-table-config';
import { useTableFooterData } from './use-table-footer-data';

export type IUseDataTableOptions<
  CustomColumns extends Record<string, ICustomCellConfigBase>
> = Omit<IUseTableConfigOptions<CustomColumns>, 'layout' | 'customFields'> & {
  layout?: IUseTableConfigOptions<CustomColumns>['layout'];
  tableStateBag: ITableStateBag;
  prefilter?: unknown[];
  defaultSort?: SortOption[];
  forceRequestFields?: Work4AllEntity;
  customFilterMapping?: CustomFilterMapping;
  additionalColumns?: DataTableColumnConfig[];
  /**
   * If set to true, will load the footer data for supported column types.
   *
   * @default false
   */
  enableFooter?: boolean;
};

export function useDataTable<
  Entity extends Work4AllEntity,
  CustomColumns extends Record<string, ICustomCellConfigBase>
>(options: IUseDataTableOptions<CustomColumns>) {
  const {
    layout = 'table',
    schema,
    tableStateBag,
    prefilter,
    defaultSort = null,
    cells,
    cellProcessors,
    forceRequestFields,
    customFilterMapping,
    additionalColumns,
    enableFooter = false,
  } = options;

  useDisableApolloAutoInvalidation();

  const { benutzerCode: userId } = useUser();

  const transformedTableFilterFields = useMemo<Filters<object>>(() => {
    return (cloneDeep(tableStateBag.tableState?.filters) || []).map(
      (filter) => {
        const colId = filter.id;
        const col = schema.columns.find((x) => x.id === colId);
        if (col?.filterField) {
          filter.id = col?.filterField;
        }
        return filter;
      }
    );
  }, [schema.columns, tableStateBag.tableState?.filters]);

  const { filter, sort } = usePrepareFilters(
    transformedTableFilterFields,
    tableStateBag.tableState?.sortBy,
    customFilterMapping,
    schema.entity
  );

  const { customFields } = useCustomFieldsConfigContext();

  const {
    fields: extractedFields,
    columnConfigs,
    cardConfig,
    prepareRowDisplayModifiers,
  } = useTableConfig({
    layout,
    schema,
    customFields,
    cells,
    cellProcessors,
    additionalColumns,
  });

  const fields = useMemo(() => {
    //eslint-disable-next-line
    //@ts-ignore
    return deepmerge(extractedFields, forceRequestFields || {});
  }, [extractedFields, forceRequestFields]);

  const searchFilter = useMemo(() => {
    if (
      !tableStateBag?.searchFilterText ||
      tableStateBag?.searchFilterText.length === 0
    ) {
      return [];
    } else {
      const filter = [];

      schema.columns.forEach((col) => {
        const isNumber =
          (col.filterable as IColumnNumberFilterConfig)?.type === 'Number';

        if (col.quickSearchable) {
          if (!isNumber || parseInt(tableStateBag?.searchFilterText))
            filter.push({
              [col.accessor]: {
                $eq: isNumber
                  ? '%' + parseInt(tableStateBag?.searchFilterText) + '%'
                  : '%' + tableStateBag?.searchFilterText?.trim() + '%',
              },
            });
        }
      });
      return filter;
    }
  }, [schema.columns, tableStateBag?.searchFilterText]);

  const finalFilter = useMemo(() => {
    const finalFilter = concat(...[filter, prefilter].filter(Boolean));

    if (searchFilter.length) {
      finalFilter.push({
        $or: searchFilter,
      });
    }

    return finalFilter;
  }, [filter, prefilter, searchFilter]);

  const requestData = useMemo(() => {
    const completeDataResponse =
      entityDefinition[schema.entity]?.remote?.withPaginationWrapper === false;

    const sortColumn = sort
      ? schema.columns.find((x) => x.id === sort.id)
      : undefined;

    const request: DataRequest = {
      filter: finalFilter.length > 0 ? finalFilter : undefined,
      entity: schema.entity as Entities,
      completeDataResponse,
      data: fields,
      sort: sort
        ? [
            {
              // sortColumn?.accessor is not defined when prefilter is made on column that is not schema
              field: sortColumn?.accessor ?? sort.id,
              direction: sort.desc
                ? SortDirection.DESCENDING
                : SortDirection.ASCENDING,
            },
          ]
        : defaultSort,
    };

    return request;
  }, [schema.entity, schema.columns, sort, finalFilter, fields, defaultSort]);

  const footerData = useTableFooterData({
    enabled: enableFooter,
    entity: schema.entity as Entities,
    columns: columnConfigs,
    filter: finalFilter,
  });

  const allColumns = useMemo(() => {
    if (!customFields || customFields.length === 0) return schema.columns;

    return [
      ...schema.columns,
      ...customFields
        .filter((field) => ALLOWED_CUSTOM_FIELD_TYPES.includes(field.fieldType))
        .map(createCustomColumn),
    ];
  }, [schema.columns, customFields]);

  const getGroupKeyFields = useCallback(
    (columnId: string) => {
      const column = allColumns.find((column) => {
        const id = column.id ?? column.accessor;
        return id === columnId;
      });

      if (!column) {
        throw new Error(`Could not find a column with id "${columnId}".`);
      }

      const { accessor, id = accessor } = column;

      return {
        id: id as string,
        label: typeof accessor === 'string' ? accessor : null,
      };
    },
    [allColumns]
  );

  const tableData = useQueryTableData(
    requestData,
    getGroupKeyFields,
    undefined,
    customFields
  );

  useRefetchOnEntityChanges({
    entity: schema.entity,
    refetch: tableData.refetch,
  });

  const readStatusModifier = useMemo(() => {
    const modifiers = schema.rowModifiers.filter(isReadStatusModifier);

    if (modifiers.length > 1) {
      console.warn(
        'Multiple ReadStatus modifiers found.' +
          ' This is a misconfiguration in the table schema.' +
          ' Only the first one will be used.'
      );
    }

    if (modifiers.length > 0) {
      return modifiers[0];
    } else {
      return null;
    }
  }, [schema]);

  const { selectedEntity, selectedEntities } = useSelectedTableRows<Entity>({
    data: tableData.data,
    tableStateBag,
  });

  const responseData = useMemo(
    () =>
      readStatusModifier
        ? {
            id: null,
            [readStatusModifier.params.field]: null,
          }
        : {},
    [readStatusModifier]
  );

  const [mutateEntity] = useDataMutation({
    entity: schema.entity as Entities,
    mutationType: EMode.upsert,
    responseData,
  });

  useEffect(() => {
    if (
      readStatusModifier !== null &&
      selectedEntity !== null &&
      get(selectedEntity, readStatusModifier.params.field) === false &&
      get(selectedEntity, readStatusModifier.params.ownerIdField) === userId &&
      'id' in selectedEntity
    ) {
      mutateEntity({
        id: selectedEntity.id,
        [readStatusModifier.params.field]: true,
      });
    }
  }, [mutateEntity, selectedEntity, userId, readStatusModifier]);

  const initialSortBy = useMemo<SortingRule<object>[]>(() => {
    if (defaultSort == null) {
      return [];
    }

    return defaultSort.map(({ field, direction }) => {
      return { id: field, desc: direction === SortDirection.DESCENDING };
    });
  }, [defaultSort]);

  useDebugValue({
    ...tableData,
    columnConfigs,
    cardConfig,
    prepareRowDisplayModifiers,
    selectedEntity,
    initialSortBy,
  });

  return {
    ...tableData,
    columnConfigs,
    cardConfig,
    prepareRowDisplayModifiers,
    selectedEntity,
    selectedEntities,
    initialSortBy,
    footerData,
  };
}
